import { useQueries } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import type { DiningListItem, DiningOpeningHours } from 'voyager';

import { getDiningCovers, isPartySizeRefusal } from '@features/tables/diningParty';
import { useTranslations } from '@lang';
import { yourJourneySelectors } from '@store/yourJourney';
import type { ParsedJourneyParams } from '@store/yourJourney/selectors';
import rewriteHttp from '@utils/rewriteHttp';

import { diningKeys, useDiningRestaurants, usePropertyContext } from './queries';
import { getRestaurantAvailabilityForDate } from './requests';
import type { DiningAvailabilityTimeslot, DiningRestaurant } from './schemas';
import { isBookableSlot } from './timeslots';

const DAYS_IN_WEEK = 7;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const LISTING_RANK = { bookable: 0, unchecked: 1, unavailable: 2 } as const;

function listingRank(unavailable: boolean, unchecked: boolean) {
  if (unavailable) return LISTING_RANK.unavailable;
  if (unchecked) return LISTING_RANK.unchecked;
  return LISTING_RANK.bookable;
}

export interface WeekDay {
  date: string;
  name: string;
}

export function getWeekFromToday(
  locale: string,
  timezone: string | undefined,
  now: Date = new Date()
): WeekDay[] {
  const today = (timezone ? moment.tz(now, timezone) : moment(now)).format('YYYY-MM-DD');
  const todayAtMiddayUtc = Date.parse(`${today}T12:00:00Z`);
  const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });

  return Array.from({ length: DAYS_IN_WEEK }, (_, offset) => {
    const day = new Date(todayAtMiddayUtc + offset * DAY_IN_MS);
    const weekday = weekdayFormat.format(day);
    return {
      date: day.toISOString().slice(0, 10),
      name: weekday.charAt(0).toLocaleUpperCase(locale) + weekday.slice(1),
    };
  });
}

function toOpeningHours(restaurant: DiningRestaurant, week: WeekDay[]): DiningOpeningHours[] {
  const { openingTime, closingTime, closedDates } = restaurant;
  if (!openingTime || !closingTime) {
    return [];
  }
  const hours = `${openingTime} - ${closingTime}`;
  const today = week[0]?.date;
  return week
    .filter(({ date }) => !closedDates.includes(date))
    .map(({ date, name }) => ({ day: name, hours, isToday: date === today }));
}

export function mapRestaurantToListItem(
  restaurant: DiningRestaurant,
  week: WeekDay[]
): DiningListItem {
  return {
    id: restaurant.providerId,
    name: restaurant.name,
    label: restaurant.subtitle ?? undefined,
    description: restaurant.description ?? undefined,
    imageUrls: restaurant.imageUrls.map((url) => rewriteHttp(url)),
    menus: restaurant.menus.flatMap((menu) =>
      menu.url ? [{ id: menu.id, title: menu.title, url: rewriteHttp(menu.url) }] : []
    ),
    openingHours: toOpeningHours(restaurant, week),
    unavailable: false,
  };
}

export function useDiningList() {
  const { locale } = useTranslations();
  const { propertyId, timezone } = usePropertyContext();
  const week = getWeekFromToday(locale, timezone);
  const { data, isInitialLoading, isError: isRestaurantsError } = useDiningRestaurants();
  const restaurants = Array.isArray(data) ? data : [];

  const journey = useSelector(yourJourneySelectors.getParsedJourneyParams) as ParsedJourneyParams;
  const rawFromDate = useSelector(yourJourneySelectors.getActualFromDateValue);
  const date =
    rawFromDate && journey.fromDate.isValid() ? journey.fromDate.format('YYYY-MM-DD') : undefined;
  const isoDate = date ?? '';
  const adults = getDiningCovers(journey);
  const children = 0;

  const availabilityQueries = useQueries({
    queries: restaurants.map((restaurant) => ({
      queryKey: diningKeys.availability(
        propertyId,
        restaurant.restaurantId,
        restaurant.restaurantName,
        isoDate,
        adults,
        children
      ),
      queryFn: () =>
        getRestaurantAvailabilityForDate(propertyId, {
          date: isoDate,
          restaurantName: restaurant.restaurantName,
          restaurantId: restaurant.restaurantId,
          adults,
          children,
        }),
      enabled: Boolean(date) && propertyId > 0,
    })),
  });

  const availabilityPending =
    Boolean(date) && availabilityQueries.some((query) => query.isInitialLoading);

  const checkFailed = (query: (typeof availabilityQueries)[number] | undefined) =>
    Boolean(query?.isError) && !isPartySizeRefusal(query?.error);

  const everyCheckFailed =
    Boolean(date) && availabilityQueries.length > 0 && availabilityQueries.every(checkFailed);
  const isError = (Boolean(isRestaurantsError) && !Array.isArray(data)) || everyCheckFailed;

  const items =
    availabilityPending || isError
      ? []
      : restaurants
          .map((restaurant, index) => {
            const query = availabilityQueries[index];
            const timeslots: DiningAvailabilityTimeslot[] = query?.data?.timeslots ?? [];
            const unchecked = Boolean(date) && checkFailed(query);
            const unavailable =
              Boolean(date) &&
              (isPartySizeRefusal(query?.error) ||
                (Boolean(query?.isSuccess) &&
                  !timeslots.some((slot) => isBookableSlot(slot, query?.data?.promotions ?? []))));
            return {
              item: { ...mapRestaurantToListItem(restaurant, week), unavailable },
              rank: listingRank(unavailable, unchecked),
            };
          })
          .sort((a, b) => a.rank - b.rank)
          .map(({ item }) => item);

  return {
    items,
    isError,
    isLoading: isInitialLoading || availabilityPending,
  };
}
